import { useCallback, useMemo } from "react";
import { useUserQuery, useOrganizationsQuery } from "./useSessionQueries";
import { useBoundStore } from "@/store";
import { DEFAULT_ORGANIZATION } from "@/constants/auth";
import { SessionData } from "@/types/auth";
import { AccessScope, Maybe, Organization } from "types";
import { authService } from "@/services/authService";

// Stable references for default values to prevent unnecessary re-renders
const EMPTY_ACCESSES: Record<string, AccessScope[]> = {};
const EMPTY_ORGANIZATIONS: Organization[] = [];

/**
 * Unified session hook - Single source of truth for authentication state
 *
 * Returns:
 * - Server data from React Query (user, organizations)
 * - Client preferences from Zustand (selectedOrganizationId)
 * - Computed values (currentOrganization, accesses, hasAccesses)
 *
 * This hook is read-only. Use useAuthActions() for mutations.
 */
export const useSession = (): SessionData => {
  // Check token to determine if queries should be running
  const token = authService.getToken();
  const hasValidToken = Boolean(token?.access_token);

  // Server state from React Query
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
  } = useUserQuery();
  const {
    data: orgsData,
    isLoading: isLoadingOrgs,
    error: orgsError,
  } = useOrganizationsQuery();

  // Client preferences from Zustand
  const selectedOrganizationId = useBoundStore(
    (state) => state.selectedOrganizationId
  );

  const error = (userError || orgsError) as Error | null;

  // Gate on initial load only — once data exists, keep it visible during refetches.
  // Using isLoading (not isFetching) prevents data from disappearing when
  // invalidateQueries triggers a background refetch.
  const bothQueriesSettled =
    !isLoadingUser && !isLoadingOrgs && userData && orgsData;

  // isLoading = true until both queries have actual data.
  // This prevents a gap where isLoading is false but data hasn't arrived yet
  // (e.g., after queryClient.clear() on logout), which would cause
  // downstream components to render with empty/stale data.
  // Give up on error: a failed query never produces data, so without this the
  // spinner would hang forever.
  const isLoading = hasValidToken && !bothQueriesSettled && !error;

  // Extract data from React Query responses
  // React Query returns data directly (not wrapped in .data like Refine)
  // Use stable default references to prevent unnecessary re-renders
  const user = bothQueriesSettled ? userData : null;
  const organizations = bothQueriesSettled ? orgsData : EMPTY_ORGANIZATIONS;
  const isAuthenticated = Boolean(user);

  // Compute current organization based on selected ID
  const currentOrganization = useMemo(() => {
    if (!organizations.length) return DEFAULT_ORGANIZATION;

    if (selectedOrganizationId) {
      const found = organizations.find(
        (org) => org.id === selectedOrganizationId
      );
      if (found) return found;
    }

    // Fallback: prefer an org the user actually belongs to. For admin users,
    // organizations contains every org they can access; user.organizations is
    // the subset that's truly theirs.
    const ownOrgId = user?.organizations?.find((id) =>
      organizations.some((org) => org.id === id)
    );
    const ownOrg = organizations.find((org) => org.id === ownOrgId);
    return ownOrg ?? organizations[0] ?? DEFAULT_ORGANIZATION;
  }, [organizations, selectedOrganizationId, user]);

  const getOrganization = useCallback(
    (id: string): Maybe<Organization> =>
      organizations.find((x) => x.id === id) ?? null,
    [organizations]
  );

  // Extract accesses from user data
  // Force new object reference to trigger useMemo in App.tsx when user changes
  const accesses = useMemo(() => {
    const userAccesses = user?.accesses?.accesses ?? EMPTY_ACCESSES;
    // Clone object to ensure new reference when user changes
    return userAccesses === EMPTY_ACCESSES
      ? EMPTY_ACCESSES
      : ({ ...userAccesses } as Record<string, AccessScope[]>);
  }, [user]);

  // Compute hasAccesses flag
  const hasAccesses = useMemo(
    () => Boolean(Object.keys(accesses).length),
    [accesses]
  );

  // Memoize the return object to prevent unnecessary re-renders
  // Only create a new object when actual values change
  return useMemo(
    () => ({
      user,
      organizations,
      currentOrganization,
      accesses,
      hasAccesses,
      isLoading,
      isAuthenticated,
      error,
      getOrganization,
    }),
    [
      user,
      organizations,
      currentOrganization,
      accesses,
      hasAccesses,
      isLoading,
      isAuthenticated,
      error,
      getOrganization,
    ]
  );
};
